import { Apps32Filled } from '@fluentui/react-icons';
import { useI18nTranslation } from '@idmc-ms-fabric/common';
import { useFabricContext } from '@idmc-ms-fabric/integration';
import { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import ErrorScreen from '../../../component/core/error/ErrorScreen';
import ProgressScreen from '../../../component/core/progress/ProgressScreen';
import i18nKeys from '../../../i18n/i18nKeys';
import { API_STATUS, TimeoutID } from '../../../utils/common-types';
import { useIDQALoggerContext } from '../IDQAlogger/IDQALoggerContext';
import { useAppRegistrationContext } from '../Registration/AppRegistrationContext';
import { TenantStatus } from './InfaLogin.type';
import InfaLoginManager, { BootoutType } from './InfaLoginManager';

type USER_ACCESS = 'READ_ONLY' | 'FULL_ACCESS' | 'NO_ACCESS';
type ContextType = {
	bootoutput: BootoutType;
	user_access: USER_ACCESS;
};

const TenantUpgradeStatus: Array<TenantStatus> = ['UpgradeFailed', 'UpgradeInProgress', 'UpgradeQueued', 'UpgradeRepairInProgress'];

const Context = createContext<ContextType | undefined>(undefined);

export const useLoginContext = (): ContextType => {
	const context = useContext(Context);
	if (!context) {
		throw `Application context cannot be used before the app context initialization`;
	}
	return context;
};

const loginRetryInterval = 2 * 1000;
const InfaLoginContext: FC<{ workspaceId: string }> = ({ workspaceId, children }) => {
	const { extensionClient } = useFabricContext();
	const { adScope } = useAppRegistrationContext();
	const { t } = useI18nTranslation();
	const timeout = useRef<TimeoutID>();
	const { logger } = useIDQALoggerContext();
	const [state, setState] = useState<{ status: API_STATUS.LOADING | API_STATUS.FAILURE } | { status: API_STATUS.SUCCESS; bootoutput: BootoutType }>({
		status: API_STATUS.LOADING,
	});

	const loginManager = useMemo(
		() =>
			new InfaLoginManager(extensionClient, adScope, workspaceId, (bootoutput) => {
				setState({
					status: API_STATUS.SUCCESS,
					bootoutput,
				});
			}),
		[extensionClient],
	);

	const init = async () => {
		try {
			const bootoutput = await loginManager.init();
			setState({
				status: API_STATUS.SUCCESS,
				bootoutput,
			});
		} catch (error) {
			logger.error('Something went wrong while initializing data quaility.', { error });
			cleanUp();
			timeout.current = setTimeout(() => init(), loginRetryInterval);
		}
	};
	const cleanUp = () => {
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
	};

	useEffect(() => {
		init();
		return () => {
			loginManager.cleanUp();
			cleanUp();
		};
	}, []);

	const user_access = useMemo(
		() =>
			state.status === API_STATUS.SUCCESS
				? state.bootoutput.status === 'Active' || state.bootoutput.status === 'UpgradeAvailable'
					? 'FULL_ACCESS'
					: TenantUpgradeStatus.includes(state.bootoutput.status)
						? 'READ_ONLY'
						: 'NO_ACCESS'
				: 'NO_ACCESS',
		[state],
	);

	return state.status === API_STATUS.SUCCESS ? (
		<Context.Provider value={{ bootoutput: state.bootoutput, user_access }}>
			{user_access === 'NO_ACCESS' ? <ErrorScreen message={t(i18nKeys.main.tenantProvisioningNoAccessMsg)} /> : children}
		</Context.Provider>
	) : state.status === API_STATUS.LOADING ? (
		<ProgressScreen icon={<Apps32Filled />} message={t(i18nKeys.main.initInfaService)} />
	) : null;
};

export default InfaLoginContext;
